// src/ui/screens/ProfileScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscribe } from '../../utils/subscribeProvider';
import { UserAvatar } from '../../components/UserAvatar';
import { ListingItem } from './post/widgets/ListingItem';
import { ProfileAtomItem } from '../../components/PostComponents/ProfileAtomItem';
import { ReportUserModal } from '../../components/ReportUserModal';
import { userProfileService } from '../../services/user/userProfileService';
import { chatService } from '../../services/chat/chatService';
import { notificationService } from '../../services/notificationService';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  increment,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  FieldValue,
  Timestamp,
} from 'firebase/firestore';
import { FullProfileImageScreen } from './FullProfileImageScreen';
import { SubscribersSubscribedScreen } from './SubscribersSubscribedScreen';
// FIX: this was previously referenced as a bare public-folder path
// ("/assets/svg/edit_profile.svg"), which 404s — every other svg icon in
// this codebase (see PostScreen.jsx: `import saleIcon from
// '../../../assets/svg/sale.svg'`) is bundled via import, not served from
// /public. That mismatch is why the edit icon never rendered.
import editProfileIcon from '../../assets/svg/edit_profile.svg';
// FIX: swapped the inline hand-drawn flag <svg> for the real report icon
// asset (same import pattern as editProfileIcon above), so it matches the
// icon used across the rest of the app instead of a generic placeholder.
import reportIcon from '../../assets/svg/report.svg';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

const PAGE_SIZE = 20;

// Desktop sidebar avatar is rendered larger than the mobile one (128px vs
// 90px). UserAvatar takes a pixel `size` prop rather than a className, so
// this can't be done with a Tailwind breakpoint alone — a small resize
// listener picks the right size. Mobile always gets 90, unchanged.
const DESKTOP_BREAKPOINT = 768;
const MOBILE_AVATAR_SIZE = 90;
const DESKTOP_AVATAR_SIZE = 128;

// ── ProfileScreen ────────────────────────────────────────────────────────────

export const ProfileScreen = () => {
  const navigate = useNavigate();
  const { userId: paramUid } = useParams();
  const { user } = useAuth();
  const { isSubscribedTo, shouldSubBack, toggleSubscribe } = useSubscribe();

  const targetUid = paramUid || user?.uid || '';
  const isOtherUser = paramUid && paramUid !== user?.uid;

  // ── Profile state ─────────────────────────────────────────────────────────

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [linksExpanded, setLinksExpanded] = useState(false);
  const [liveSubscriberCount, setLiveSubscriberCount] = useState(0);
  const [liveSubscribedCount, setLiveSubscribedCount] = useState(0);
  const [isSeller, setIsSeller] = useState(false);
  const [checkingSellerStatus, setCheckingSellerStatus] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  const [verificationData, setVerificationData] = useState(null);
  const [loadingVerification, setLoadingVerification] = useState(true);
  const [isSubscribedToNotifications, setIsSubscribedToNotifications] = useState(false);
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);
  const [isSharingProfile, setIsSharingProfile] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [topLevelVerified, setTopLevelVerified] = useState(false);
  const [affiliateDocs, setAffiliateDocs] = useState([]);
  const [loadingAffiliates, setLoadingAffiliates] = useState(true);
  // Desktop-only avatar sizing — see DESKTOP_AVATAR_SIZE note above.
  const [avatarSize, setAvatarSize] = useState(MOBILE_AVATAR_SIZE);

  // ── Tab state ─────────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState(0);
  const tabs = isOtherUser ? ['Posts', 'Atoms'] : ['Posts', 'Liked', 'Saved'];

  // ── Posted listings ──────────────────────────────────────────────────────

  const [postedDocs, setPostedDocs] = useState([]);
  const [loadingPostedInitial, setLoadingPostedInitial] = useState(true);
  const [loadingPostedMore, setLoadingPostedMore] = useState(false);
  const [hasMorePosted, setHasMorePosted] = useState(true);
  const postedLastDocRef = useRef(null);
  const postedScrollRef = useRef(null);
  const loadingPostedMoreRef = useRef(false);
  const hasMorePostedRef = useRef(true);

  // ── Liked listings ───────────────────────────────────────────────────────

  const [likedDocs, setLikedDocs] = useState([]);
  const [loadingLikedInitial, setLoadingLikedInitial] = useState(true);
  const [loadingLikedMore, setLoadingLikedMore] = useState(false);
  const [hasMoreLiked, setHasMoreLiked] = useState(true);
  const likedLastDocRef = useRef(null);
  const likedScrollRef = useRef(null);
  const loadingLikedMoreRef = useRef(false);
  const hasMoreLikedRef = useRef(true);

  // ── Saved listings ───────────────────────────────────────────────────────

  const [savedDocs, setSavedDocs] = useState([]);
  const [loadingSavedInitial, setLoadingSavedInitial] = useState(true);
  const [loadingSavedMore, setLoadingSavedMore] = useState(false);
  const [hasMoreSaved, setHasMoreSaved] = useState(true);
  const savedLastDocRef = useRef(null);
  const savedScrollRef = useRef(null);
  const loadingSavedMoreRef = useRef(false);
  const hasMoreSavedRef = useRef(true);

  // ── Atoms ─────────────────────────────────────────────────────────────────

  const [atomsDocs, setAtomsDocs] = useState([]);
  const [loadingAtomsInitial, setLoadingAtomsInitial] = useState(true);
  const [loadingAtomsMore, setLoadingAtomsMore] = useState(false);
  const [hasMoreAtoms, setHasMoreAtoms] = useState(true);
  const atomsLastDocRef = useRef(null);
  const atomsScrollRef = useRef(null);
  const loadingAtomsMoreRef = useRef(false);
  const hasMoreAtomsRef = useRef(true);

  // ── Responsive avatar size ───────────────────────────────────────────────
  // Mobile layout/markup is untouched; this only swaps the avatar's pixel
  // size once the viewport crosses into the desktop layout below.

  useEffect(() => {
    const updateAvatarSize = () => {
      setAvatarSize(window.innerWidth >= DESKTOP_BREAKPOINT ? DESKTOP_AVATAR_SIZE : MOBILE_AVATAR_SIZE);
    };
    updateAvatarSize();
    window.addEventListener('resize', updateAvatarSize);
    return () => window.removeEventListener('resize', updateAvatarSize);
  }, []);

  // ── Profile subscription ─────────────────────────────────────────────────

  useEffect(() => {
    if (!targetUid) return;

    const unsubscribe = onSnapshot(doc(db, 'users', targetUid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setLiveSubscriberCount((data.subscribers || []).length);
        setLiveSubscribedCount((data.subscribed || []).length);
      }
    }, (error) => {
      console.error('Profile subscription error:', error);
    });

    return () => unsubscribe();
  }, [targetUid]);

  // ── Load profile ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const profileData = await userProfileService.getProfileByUid(targetUid);
        setProfile(profileData);
      } catch (error) {
        console.error('Load profile error:', error);
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, [targetUid]);

  // ── Check admin status ────────────────────────────────────────────────────

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setAdminCheckLoading(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data().isAdmin || false);
        }
      } catch (error) {
        console.error('Admin check error:', error);
      } finally {
        setAdminCheckLoading(false);
      }
    };
    checkAdmin();
  }, [user]);

  // ── Check seller status ──────────────────────────────────────────────────

  useEffect(() => {
    const checkSeller = async () => {
      if (!targetUid || !isOtherUser) {
        setCheckingSellerStatus(false);
        return;
      }
      try {
        const q = query(
          collection(db, 'listings'),
          where('uid', '==', targetUid),
          limit(1)
        );
        const snap = await getDocs(q);
        setIsSeller(!snap.empty);
      } catch (error) {
        console.error('Check seller error:', error);
        setIsSeller(false);
      } finally {
        setCheckingSellerStatus(false);
      }
    };
    checkSeller();
  }, [targetUid, isOtherUser]);

  // ── Load verification data ──────────────────────────────────────────────

  useEffect(() => {
    const loadVerification = async () => {
      if (!targetUid) {
        setLoadingVerification(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', targetUid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setVerificationData(data.verification || null);
          setTopLevelVerified(data.isVerified === true);
        }
      } catch (error) {
        console.error('Load verification error:', error);
      } finally {
        setLoadingVerification(false);
      }
    };
    loadVerification();
  }, [targetUid]);

  // ── Load affiliates ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!targetUid) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'affiliations'),
        where('participants', 'array-contains', targetUid),
        where('status', '==', 'approved')
      ),
      (snap) => {
        setAffiliateDocs(snap.docs.map(d => d.data()));
        setLoadingAffiliates(false);
      },
      (error) => {
        console.error('Affiliates stream error:', error);
        setLoadingAffiliates(false);
      }
    );

    return () => unsubscribe();
  }, [targetUid]);

  // ── Load posted listings ─────────────────────────────────────────────────

  const loadPosted = useCallback(async (loadMore = false) => {
    if (!targetUid) return;
    if (loadMore && (loadingPostedMoreRef.current || !hasMorePostedRef.current)) return;

    if (!loadMore) {
      setLoadingPostedInitial(true);
      setPostedDocs([]);
      postedLastDocRef.current = null;
      hasMorePostedRef.current = true;
      setHasMorePosted(true);
    } else {
      loadingPostedMoreRef.current = true;
      setLoadingPostedMore(true);
    }

    try {
      let q = query(
        collection(db, 'listings'),
        where('uid', '==', targetUid),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      if (loadMore && postedLastDocRef.current) {
        q = query(q, startAfter(postedLastDocRef.current));
      }

      const snap = await getDocs(q);
      const docs = snap.docs;

      if (loadMore) {
        setPostedDocs(prev => [...prev, ...docs]);
      } else {
        setPostedDocs(docs);
      }

      const stillHasMore = docs.length === PAGE_SIZE;
      hasMorePostedRef.current = stillHasMore;
      setHasMorePosted(stillHasMore);
      if (docs.length > 0) {
        postedLastDocRef.current = docs[docs.length - 1];
      }
    } catch (error) {
      console.error('Load posted error:', error);
    } finally {
      if (loadMore) {
        loadingPostedMoreRef.current = false;
        setLoadingPostedMore(false);
      } else {
        setLoadingPostedInitial(false);
      }
    }
  }, [targetUid]);

  // ── Load liked listings ──────────────────────────────────────────────────

  const loadLiked = useCallback(async (loadMore = false) => {
    if (!user) return;
    if (loadMore && (loadingLikedMoreRef.current || !hasMoreLikedRef.current)) return;

    if (!loadMore) {
      setLoadingLikedInitial(true);
      setLikedDocs([]);
      likedLastDocRef.current = null;
      hasMoreLikedRef.current = true;
      setHasMoreLiked(true);
    } else {
      loadingLikedMoreRef.current = true;
      setLoadingLikedMore(true);
    }

    try {
      let q = query(
        collection(db, 'listings'),
        where('likedBy', 'array-contains', user.uid),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      if (loadMore && likedLastDocRef.current) {
        q = query(q, startAfter(likedLastDocRef.current));
      }

      const snap = await getDocs(q);
      const docs = snap.docs;

      if (loadMore) {
        setLikedDocs(prev => [...prev, ...docs]);
      } else {
        setLikedDocs(docs);
      }

      const stillHasMore = docs.length === PAGE_SIZE;
      hasMoreLikedRef.current = stillHasMore;
      setHasMoreLiked(stillHasMore);
      if (docs.length > 0) {
        likedLastDocRef.current = docs[docs.length - 1];
      }
    } catch (error) {
      console.error('Load liked error:', error);
    } finally {
      if (loadMore) {
        loadingLikedMoreRef.current = false;
        setLoadingLikedMore(false);
      } else {
        setLoadingLikedInitial(false);
      }
    }
  }, [user]);

  // ── Load saved listings ──────────────────────────────────────────────────

  const loadSaved = useCallback(async (loadMore = false) => {
    if (!user) return;
    if (loadMore && (loadingSavedMoreRef.current || !hasMoreSavedRef.current)) return;

    if (!loadMore) {
      setLoadingSavedInitial(true);
      setSavedDocs([]);
      savedLastDocRef.current = null;
      hasMoreSavedRef.current = true;
      setHasMoreSaved(true);
    } else {
      loadingSavedMoreRef.current = true;
      setLoadingSavedMore(true);
    }

    try {
      let q = query(
        collection(db, 'listings'),
        where('savedBy', 'array-contains', user.uid),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      if (loadMore && savedLastDocRef.current) {
        q = query(q, startAfter(savedLastDocRef.current));
      }

      const snap = await getDocs(q);
      const docs = snap.docs;

      if (loadMore) {
        setSavedDocs(prev => [...prev, ...docs]);
      } else {
        setSavedDocs(docs);
      }

      const stillHasMore = docs.length === PAGE_SIZE;
      hasMoreSavedRef.current = stillHasMore;
      setHasMoreSaved(stillHasMore);
      if (docs.length > 0) {
        savedLastDocRef.current = docs[docs.length - 1];
      }
    } catch (error) {
      console.error('Load saved error:', error);
    } finally {
      if (loadMore) {
        loadingSavedMoreRef.current = false;
        setLoadingSavedMore(false);
      } else {
        setLoadingSavedInitial(false);
      }
    }
  }, [user]);

  // ── Load atoms ────────────────────────────────────────────────────────────

  const loadAtoms = useCallback(async (loadMore = false) => {
    if (!targetUid) return;
    if (loadMore && (loadingAtomsMoreRef.current || !hasMoreAtomsRef.current)) return;

    if (!loadMore) {
      setLoadingAtomsInitial(true);
      setAtomsDocs([]);
      atomsLastDocRef.current = null;
      hasMoreAtomsRef.current = true;
      setHasMoreAtoms(true);
    } else {
      loadingAtomsMoreRef.current = true;
      setLoadingAtomsMore(true);
    }

    try {
      let q = query(
        collection(db, 'listings'),
        where('uid', '==', targetUid),
        where('hasVideo', '==', true),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      if (loadMore && atomsLastDocRef.current) {
        q = query(q, startAfter(atomsLastDocRef.current));
      }

      const snap = await getDocs(q);
      const docs = snap.docs;

      if (loadMore) {
        setAtomsDocs(prev => [...prev, ...docs]);
      } else {
        setAtomsDocs(docs);
      }

      const stillHasMore = docs.length === PAGE_SIZE;
      hasMoreAtomsRef.current = stillHasMore;
      setHasMoreAtoms(stillHasMore);
      if (docs.length > 0) {
        atomsLastDocRef.current = docs[docs.length - 1];
      }
    } catch (error) {
      console.error('Load atoms error:', error);
    } finally {
      if (loadMore) {
        loadingAtomsMoreRef.current = false;
        setLoadingAtomsMore(false);
      } else {
        setLoadingAtomsInitial(false);
      }
    }
  }, [targetUid]);

  // ── Initial load ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (targetUid) {
      loadPosted(false);
      if (isOtherUser) {
        loadAtoms(false);
      } else {
        loadLiked(false);
        loadSaved(false);
      }
    }
  }, [targetUid, isOtherUser, loadPosted, loadAtoms, loadLiked, loadSaved]);

  // ── Scroll handlers ──────────────────────────────────────────────────────

  const handlePostedScroll = useCallback(() => {
    if (!postedScrollRef.current) return;
    const el = postedScrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
      if (!loadingPostedMore && hasMorePosted) {
        loadPosted(true);
      }
    }
  }, [loadingPostedMore, hasMorePosted, loadPosted]);

  const handleLikedScroll = useCallback(() => {
    if (!likedScrollRef.current) return;
    const el = likedScrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
      if (!loadingLikedMore && hasMoreLiked) {
        loadLiked(true);
      }
    }
  }, [loadingLikedMore, hasMoreLiked, loadLiked]);

  const handleSavedScroll = useCallback(() => {
    if (!savedScrollRef.current) return;
    const el = savedScrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
      if (!loadingSavedMore && hasMoreSaved) {
        loadSaved(true);
      }
    }
  }, [loadingSavedMore, hasMoreSaved, loadSaved]);

  const handleAtomsScroll = useCallback(() => {
    if (!atomsScrollRef.current) return;
    const el = atomsScrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
      if (!loadingAtomsMore && hasMoreAtoms) {
        loadAtoms(true);
      }
    }
  }, [loadingAtomsMore, hasMoreAtoms, loadAtoms]);

  // ── Refresh all data ──────────────────────────────────────────────────────

  const refreshAll = useCallback(async () => {
    setProfileLoading(true);
    try {
      const profileData = await userProfileService.getProfileByUid(targetUid);
      setProfile(profileData);
    } catch (error) {
      console.error('Refresh profile error:', error);
    } finally {
      setProfileLoading(false);
    }
    loadPosted(false);
    if (isOtherUser) {
      loadAtoms(false);
    } else {
      loadLiked(false);
      loadSaved(false);
    }
  }, [targetUid, isOtherUser, loadPosted, loadAtoms, loadLiked, loadSaved]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSubscribe = async () => {
    if (!user) {
      alert('Sign in to subscribe');
      return;
    }
    if (!profile) return;

    const wasSubscribed = isSubscribedTo(profile.uid);
    await toggleSubscribe(profile.uid);

    if (wasSubscribed && isSubscribedToNotifications) {
      try {
        await updateDoc(doc(db, 'users', profile.uid), {
          notificationSubscribers: arrayRemove(user.uid),
        });
        setIsSubscribedToNotifications(false);
      } catch (error) {
        console.error('Remove notification subscriber error:', error);
      }
    }

    if (!wasSubscribed) {
      try {
        const userDoc = await getDoc(doc(db, 'users', profile.uid));
        if (userDoc.exists()) {
          const subs = userDoc.data().notificationSubscribers || [];
          setIsSubscribedToNotifications(subs.includes(user.uid));
        }
      } catch (error) {
        console.error('Refresh notification status error:', error);
      }
    }
  };

  const handleNotifyToggle = async () => {
    if (!user || !profile) return;

    if (!isSubscribedTo(profile.uid)) {
      alert('Subscribe first to enable notifications');
      return;
    }

    setIsNotifyLoading(true);
    try {
      const ref = doc(db, 'users', profile.uid);
      if (isSubscribedToNotifications) {
        await updateDoc(ref, {
          notificationSubscribers: arrayRemove(user.uid),
        });
        setIsSubscribedToNotifications(false);
        alert('Notifications turned off');
      } else {
        await updateDoc(ref, {
          notificationSubscribers: arrayUnion(user.uid),
        });
        setIsSubscribedToNotifications(true);
        alert('Notifications turned on');
      }
    } catch (error) {
      console.error('Notify toggle error:', error);
      alert('Failed to update notifications');
    } finally {
      setIsNotifyLoading(false);
    }
  };

  const handleOpenChat = () => {
    if (!user) {
      alert('Please sign in to chat');
      return;
    }
    if (!profile) return;

    if (user.uid === profile.uid) {
      alert('You cannot chat with yourself');
      return;
    }

    const conversationId = [user.uid, profile.uid].sort().join('-');

    navigate(`/chat/${conversationId}`, {
      state: {
        receiverId: profile.uid,
        title: profile.displayName,
        avatarUrl: profile.hasProfileImage ? profile.profileImage : null,
      },
    });
  };

  const handleCall = () => {
    const phone = profile?.phone;
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleShareProfile = async () => {
    if (isSharingProfile) return;
    setIsSharingProfile(true);
    try {
      const uid = targetUid || '';
      const name = profile?.displayName || 'this user';
      const url = `https://filatom.com/profile/${uid}`;
      if (navigator.share) {
        await navigator.share({
          title: `${name}'s profile on Filatom`,
          text: `Check out ${name}'s profile on Filatom!`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(`Check out ${name}'s profile on Filatom!\n${url}`);
        alert('Profile link copied to clipboard!');
      }
      await updateDoc(doc(db, 'users', uid), {
        shareCount: increment(1),
      });
    } catch (error) {
      console.error('Share profile error:', error);
    } finally {
      setIsSharingProfile(false);
    }
  };

  const handleCopyUsername = () => {
    const username = profile?.username || '';
    if (username) {
      navigator.clipboard.writeText(`@${username}`);
      alert(`@${username} copied to clipboard`);
    }
  };

  const handleLaunchLink = (url) => {
    const raw = url.trim();
    const fullUrl = raw.startsWith('http') ? raw : `https://${raw}`;
    window.open(fullUrl, '_blank');
  };

  const handleReportUser = () => {
    setShowReportModal(true);
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, 'listings', listingId));
      setPostedDocs(prev => prev.filter(d => d.id !== listingId));
    } catch (error) {
      console.error('Delete listing error:', error);
      alert('Failed to delete listing');
    }
  };

  const handleEditListing = (listing) => {
    navigate('/edit-listing', {
      state: {
        listingId: listing.id,
        listing: listing,
      },
    });
  };

  const isListingEditable = (createdAt) => {
    if (!createdAt) return false;
    const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const diff = (Date.now() - date.getTime()) / 1000;
    return diff < 3600;
  };

  // ── Navigation methods ──────────────────────────────────────────────────

  const navigateToSubscribers = (initialTabIndex = 0) => {
    navigate(`/profile/${targetUid}/subscribers`, {
      state: {
        displayName: profile?.displayName,
        initialTabIndex,
      },
    });
  };

  const navigateToFullImage = () => {
    if (!profile) return;
    const imageUrl = profile.hasProfileImage ? profile.profileImage : '';
    if (!imageUrl) return;
    navigate(`/profile/${targetUid}/image`, {
      state: {
        imageUrl,
        name: profile.displayName,
      },
    });
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return String(count);
  };

  const prepareDoc = (doc) => {
    const data = { id: doc.id, ...doc.data() };
    if (!data.uid && data.userRef) data.uid = data.userRef;
    return data;
  };

  const getAffiliatePartners = () => {
    if (!targetUid) return [];
    return affiliateDocs
      .map((data) => {
        const isRequester = data.requesterUid === targetUid;
        const otherUid = isRequester ? data.targetUid : data.requesterUid;
        const otherName = isRequester ? data.targetName : data.requesterName;
        const otherUsername = isRequester ? data.targetUsername : data.requesterUsername;
        const label = otherName || otherUsername || '';
        return { uid: otherUid, name: label };
      })
      .filter((m) => m.uid && m.name);
  };

  const renderStatusBadge = () => {
    if (loadingVerification) return null;

    if (!isOtherUser && verificationData) {
      const status = verificationData.status;

      if (status === 'approved') {
        return (
          <div className="mt-1.5 px-2.5 py-1 rounded-full inline-flex items-center gap-1 border" style={{ backgroundColor: 'rgba(76,175,80,0.15)', borderColor: 'rgba(76,175,80,0.3)' }}>
            <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-500 text-[11px] font-semibold">Approved</span>
          </div>
        );
      } else if (status === 'pending') {
        return (
          <div className="mt-1.5 px-2.5 py-1 rounded-full inline-flex items-center gap-1 border" style={{ backgroundColor: 'rgba(255,193,7,0.15)', borderColor: 'rgba(255,193,7,0.3)' }}>
            <svg className="w-3 h-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" strokeWidth={2} />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
            </svg>
            <span className="text-yellow-500 text-[11px] font-semibold">Pending</span>
          </div>
        );
      } else if (status === 'rejected') {
        return (
          <div className="mt-1.5 px-2.5 py-1 rounded-full inline-flex items-center gap-1 border" style={{ backgroundColor: 'rgba(244,67,54,0.15)', borderColor: 'rgba(244,67,54,0.3)' }}>
            <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-red-500 text-[11px] font-semibold">Rejected</span>
          </div>
        );
      }
    }

    if (isOtherUser && verificationData && verificationData.status === 'approved') {
      const sellerCategory = verificationData.sellerCategory;
      if (sellerCategory) {
        return (
          <div className="mt-1.5 px-2.5 py-1 rounded-full inline-flex items-center gap-1 border" style={{ backgroundColor: `${COLORS.orange}1F`, borderColor: `${COLORS.orange}4D` }}>
            <svg className="w-3 h-3 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-filatom-primary text-[11px] font-semibold">{sellerCategory}</span>
          </div>
        );
      }
    }

    return null;
  };

  // ── Render affiliates row ──────────────────────────────────────────

  const renderAffiliatesRow = () => {
    const partners = getAffiliatePartners();
    if (partners.length === 0 || loadingAffiliates) return null;

    const categoryType = verificationData?.categoryType;
    const isBusinessProfile = categoryType === 'business';
    const prefix = isBusinessProfile ? 'Employing' : 'Affiliated to';

    return (
      <div className="flex flex-wrap justify-center md:justify-start gap-x-1 gap-y-0.5 mt-2 px-6 md:px-0">
        <span className="text-white/40 text-sm">{prefix}</span>
        {partners.map((partner, index) => (
          <React.Fragment key={partner.uid}>
            <button
              onClick={() => navigate(`/profile/${partner.uid}`)}
              className="text-filatom-primary text-sm font-bold hover:underline"
            >
              {partner.name}
            </button>
            {index < partners.length - 1 && (
              <span className="text-white/40 text-sm">,</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderStatsRow = () => {
    return (
      <div className="flex justify-center gap-4 md:gap-8">
        {/* Subscribers - clickable */}
        <button
          onClick={() => navigateToSubscribers(0)}
          className="flex flex-col items-center px-4 md:px-0 hover:opacity-80 transition-opacity"
        >
          <span className="text-white text-lg font-bold">{formatCount(liveSubscriberCount)}</span>
          <span className="text-white/50 text-[11px]">Subscribers</span>
        </button>
        <div className="w-px h-6 bg-white/10" />
        {/* Subscribed - clickable */}
        <button
          onClick={() => navigateToSubscribers(1)}
          className="flex flex-col items-center px-4 md:px-0 hover:opacity-80 transition-opacity"
        >
          <span className="text-white text-lg font-bold">{formatCount(liveSubscribedCount)}</span>
          <span className="text-white/50 text-[11px]">Subscribed</span>
        </button>
        <div className="w-px h-6 bg-white/10" />
        <div className="flex flex-col items-center px-4 md:px-0">
          <span className="text-white text-lg font-bold">{formatCount(postedDocs.length)}</span>
          <span className="text-white/50 text-[11px]">Ads</span>
        </div>
      </div>
    );
  };

  const renderOtherUserActions = () => {
    const isSubscribed = isSubscribedTo(profile?.uid || '');
    const userShouldSubBack = shouldSubBack(profile?.uid || '');
    const subLabel = isSubscribed ? 'Subscribed' : (userShouldSubBack ? 'Sub Back' : 'Subscribe');
    const subColor = isSubscribed ? COLORS.card : COLORS.orange;
    const showCallButton = isSeller && !checkingSellerStatus;
    const showReportInActionRow = !isSeller && !checkingSellerStatus;

    // FIX: buttons now get `whitespace-nowrap` + slightly taller 44px height
    // so "Subscribe"/"Subscribed"/"Sub Back" and "Message" never look
    // cramped or risk wrapping at narrower viewport widths — matching how
    // roomy these pills look in the Flutter version's fixed-height Row.
    return (
      <div className="flex gap-2 px-4 md:px-0 w-full">
        <button
          onClick={handleSubscribe}
          className="flex-[5] h-11 rounded-full flex items-center justify-center text-white text-sm font-semibold whitespace-nowrap transition-colors"
          style={{
            backgroundColor: subColor,
            border: isSubscribed ? '1px solid rgba(255,255,255,0.07)' : 'none',
          }}
        >
          {subLabel}
        </button>
        <button
          onClick={handleOpenChat}
          className="flex-[4] h-11 rounded-full flex items-center justify-center text-white text-sm font-semibold whitespace-nowrap"
          style={{ backgroundColor: COLORS.card }}
        >
          Message
        </button>
        {showCallButton && (
          <button
            onClick={handleCall}
            className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: COLORS.card }}
          >
            <svg className="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        )}
        {showReportInActionRow && (
          <button
            onClick={handleReportUser}
            className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: COLORS.card }}
          >
            {/* FIX: real report.svg asset instead of inline flag path,
                forced white via brightness-0 invert since the source svg
                is black by default. */}
            <img src={reportIcon} alt="Report" className="w-[18px] h-[18px] filter brightness-0 invert" />
          </button>
        )}
      </div>
    );
  };

  // ── Render tabs ────────────────────────────────────────────────────────────

  const renderPostedTab = () => {
    if (loadingPostedInitial) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (postedDocs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <svg className="w-[52px] h-[52px] text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-white/35 text-sm font-medium">No listings yet</p>
          {!isOtherUser && (
            <button
              onClick={() => navigate('/post/add-property')}
              className="px-5 py-2.5 rounded-full bg-filatom-primary text-white text-sm font-semibold"
            >
              Add your first property
            </button>
          )}
        </div>
      );
    }

    return (
      <div
        ref={postedScrollRef}
        onScroll={handlePostedScroll}
        className="h-full overflow-y-auto pb-20"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-4 px-2.5 md:px-0">
          {postedDocs.map((doc) => {
            const listing = prepareDoc(doc);
            return (
              <div key={doc.id} className="relative">
                <ListingItem listing={listing} />
                {!isOtherUser && (
                  <button
                    onClick={() => {
                      if (isListingEditable(listing.createdAt)) {
                        handleEditListing(listing);
                      } else {
                        handleDeleteListing(listing.id);
                      }
                    }}
                    className="absolute top-1.5 left-1.5 w-[26px] h-[26px] rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                  >
                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="12" cy="19" r="2" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {loadingPostedMore && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  };

  const renderLikedTab = () => {
    if (loadingLikedInitial) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (likedDocs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <svg className="w-[52px] h-[52px] text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <p className="text-white/35 text-sm font-medium">No liked listings yet</p>
        </div>
      );
    }

    return (
      <div
        ref={likedScrollRef}
        onScroll={handleLikedScroll}
        className="h-full overflow-y-auto pb-20"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-4 px-2.5 md:px-0">
          {likedDocs.map((doc) => (
            <ListingItem key={doc.id} listing={prepareDoc(doc)} />
          ))}
        </div>
        {loadingLikedMore && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  };

  const renderSavedTab = () => {
    if (loadingSavedInitial) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (savedDocs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <svg className="w-[52px] h-[52px] text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <p className="text-white/35 text-sm font-medium">No saved listings yet</p>
        </div>
      );
    }

    return (
      <div
        ref={savedScrollRef}
        onScroll={handleSavedScroll}
        className="h-full overflow-y-auto pb-20"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-4 px-2.5 md:px-0">
          {savedDocs.map((doc) => (
            <ListingItem key={doc.id} listing={prepareDoc(doc)} />
          ))}
        </div>
        {loadingSavedMore && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  };

  const renderAtomsTab = () => {
    if (loadingAtomsInitial) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (atomsDocs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <svg className="w-[52px] h-[52px] text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p className="text-white/35 text-sm font-medium">No atoms yet</p>
        </div>
      );
    }

    const atoms = atomsDocs.map(d => prepareDoc(d));

    return (
      <div
        ref={atomsScrollRef}
        onScroll={handleAtomsScroll}
        className="h-full overflow-y-auto pb-20"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-4 px-2.5 md:px-0">
          {atoms.map((listing, index) => (
            <ProfileAtomItem
              key={listing.id}
              listing={listing}
              index={index}
              allListings={atoms}
            />
          ))}
        </div>
        {loadingAtomsMore && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  const displayName = profileLoading ? '...' : (profile?.displayName || 'User');
  const username = profileLoading ? '' : (profile?.username || '');
  const bioText = profile?.bio || '';
  const links = profile?.links || [];
  const hasCard = (bioText || links.length > 0);
  const isVerified = topLevelVerified;
  const isVerifiedSeller = verificationData?.status === 'approved' && verificationData?.sellerCategory;

  const statusBarHeight = 44;
  const topBarHeight = statusBarHeight + 56 + 8;

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Top Bar
          Desktop: the bar itself stays fixed/full-bleed (unchanged behavior),
          but its contents are now centered to the same max-w-6xl column as
          the rest of the page, with a hairline bottom border + blurred
          background so it reads as a proper app chrome bar instead of a
          mobile header stretched across a wide viewport. */}
      <div
        className="fixed top-0 left-0 right-0 z-10 px-1 pt-safe-top md:border-b md:border-white/5 md:bg-filatom-bg/85 md:backdrop-blur-xl"
        style={{ paddingTop: 12 }}
      >
        <div className="flex items-center justify-between h-14 md:h-16 px-1 md:max-w-6xl md:mx-auto md:px-10">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(35,38,44,0.3)',
                backdropFilter: 'blur(24px)',
                border: '1px solid #23262C',
              }}
            >
              <svg className="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {isOtherUser && isSeller && !checkingSellerStatus && (
              <button
                onClick={handleReportUser}
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid #23262C',
                }}
              >
                {/* FIX: real report.svg asset, forced white */}
                <img src={reportIcon} alt="Report" className="w-[18px] h-[18px] filter brightness-0 invert" />
              </button>
            )}
            {isAdmin && !adminCheckLoading && !isOtherUser && (
              <button
                onClick={() => {
                  const action = window.confirm(
                    'Admin Panel:\n\n- Click OK to go to Verification Management\n- Cancel to go to Create Ad'
                  );
                  if (action) {
                    navigate('/verifications');
                  } else {
                    navigate('/post/create');
                  }
                }}
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid #23262C',
                }}
              >
                <svg className="w-[18px] h-[18px] text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>
            )}
          </div>

          {!isOtherUser && (
            <span className="text-white text-xl md:text-lg md:font-medium md:tracking-wide font-semibold">Channel</span>
          )}

          <div className="flex items-center gap-1">
            {!isOtherUser ? (
              <button
                onClick={() => navigate('/edit-profile')}
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid #23262C',
                }}
              >
                <img
                  src={editProfileIcon}
                  alt="Edit"
                  className="w-6 h-6 filter brightness-0 invert"
                />
              </button>
            ) : (
              <button
                onClick={handleNotifyToggle}
                disabled={isNotifyLoading}
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid #23262C',
                }}
              >
                {isNotifyLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: isSubscribedToNotifications ? COLORS.orange : '#FFFFFF' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                )}
              </button>
            )}
            <button
              onClick={handleShareProfile}
              disabled={isSharingProfile}
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(35,38,44,0.3)',
                backdropFilter: 'blur(24px)',
                border: '1px solid #23262C',
              }}
            >
              {isSharingProfile ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content
          Desktop: the whole page is contained to a centered max-w-6xl
          column (instead of the mobile view's edge-to-edge width), and the
          profile header switches from a single centered stack into a
          two-column layout — a sticky left-hand identity/bio sidebar next
          to a right-hand tabs + listings column. All of this rides on
          `md:` variants layered onto the original mobile classes, so
          nothing below md:768px changes from the original markup. */}
      <div className="pt-[72px] md:pt-28 md:max-w-6xl md:mx-auto md:px-10 md:pb-16">
        <div className="flex flex-col items-center md:flex-row md:items-start md:gap-14 px-4 md:px-0">

          {/* Sidebar (desktop) / Profile header (mobile) */}
          <div className="flex flex-col items-center md:items-start md:w-[300px] md:flex-shrink-0 md:sticky md:top-28 md:pb-10">
            {profileLoading ? (
              <div
                className="rounded-full bg-filatom-cardLight animate-pulse"
                style={{ width: avatarSize, height: avatarSize }}
              />
            ) : (
              <button
                onClick={navigateToFullImage}
                className="focus:outline-none"
              >
                <UserAvatar
                  size={avatarSize}
                  imageUrl={profile?.hasProfileImage ? profile.profileImage : null}
                  name={displayName}
                />
              </button>
            )}
            <div className="flex items-center gap-1 mt-2 md:mt-4">
              <h2 className="text-white text-xl md:text-2xl font-bold">{displayName}</h2>
              {isVerified && (
                <svg className="w-[18px] h-[18px] text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              )}
            </div>
            {renderStatusBadge()}
            {username && (
              <div className="flex items-center gap-1 mt-0.5">
                <button onClick={handleCopyUsername} className="text-white/40 text-sm">
                  @{username}
                </button>
                {isVerifiedSeller && (
                  <div className="px-1.5 py-0.5 rounded-full bg-filatom-primary/10 border border-filatom-primary/20 text-filatom-primary text-[9px] font-semibold">
                    ID Verified
                  </div>
                )}
              </div>
            )}
            <div className="mt-2.5 md:mt-5 md:w-full">{renderStatsRow()}</div>

            {/* Affiliates Row */}
            {renderAffiliatesRow()}

            {isOtherUser && (
              <div className="mt-3 md:mt-4 md:w-full">
                {renderOtherUserActions()}
              </div>
            )}

            {!profileLoading && hasCard && (
              <div className="w-full mt-2.5 md:mt-5 p-3.5 md:p-5 rounded-xl" style={{ backgroundColor: COLORS.card }}>
                {bioText && (
                  <button
                    onClick={() => setBioExpanded(!bioExpanded)}
                    className="text-left w-full"
                  >
                    <p className={`text-white text-sm leading-relaxed ${!bioExpanded ? 'line-clamp-3' : ''}`}>
                      {bioText}
                      {!bioExpanded && bioText.length > 100 && (
                        <span className="text-filatom-primary"> read more</span>
                      )}
                    </p>
                  </button>
                )}
                {links.length > 0 && (
                  <div className="mt-2.5 space-y-2">
                    {links.map((link, index) => (
                      <button
                        key={index}
                        onClick={() => handleLaunchLink(link)}
                        className="flex items-center gap-1.5 w-full"
                      >
                        <svg className="w-4 h-4 text-filatom-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span className="text-filatom-primary text-sm truncate">{link}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main column (desktop) / Tabs + listings (mobile, unchanged) */}
          <div className="w-full md:flex-1 md:min-w-0">
            {/* Tab Bar */}
            <div className="sticky top-[72px] md:top-28 z-5 bg-filatom-bg">
              <div className="flex border-b border-white/5 mt-3 md:mt-0">
                {tabs.map((tab, index) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(index)}
                    className={`flex-1 md:flex-none py-3 md:py-4 md:px-8 text-sm font-medium transition-colors relative ${
                      activeTab === index ? 'text-white' : 'text-white/40'
                    }`}
                  >
                    {tab}
                    {activeTab === index && (
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full" style={{ backgroundColor: COLORS.orange }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="h-[calc(100vh-220px)] md:h-[calc(100vh-190px)]">
              {activeTab === 0 && renderPostedTab()}
              {activeTab === 1 && (isOtherUser ? renderAtomsTab() : renderLikedTab())}
              {activeTab === 2 && renderSavedTab()}
            </div>
          </div>

        </div>
      </div>

      {/* Report Modal */}
      {/* FIX: previously ReportUserModal was mounted inline at the bottom
          of the normal document flow with no positioning wrapper, so on
          web it visually appeared "stuck at the bottom" of the page
          instead of overlaying the screen like Flutter's
          showModalBottomSheet does. This wraps it in a proper fixed,
          full-viewport, centered, dimmed backdrop so it now always
          overlays on top regardless of scroll position. */}
      {showReportModal && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <ReportUserModal
            reportedUserId={profile.uid}
            reportedUserName={profile.displayName}
            onClose={() => setShowReportModal(false)}
          />
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;